import React, { useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../../Components/Toaster/Toaster";
import { updateStoreInfo } from "../../../../lib/utils/apiCalls";
import TickMark from "../../../../Assets/Modals/TickMark.svg";
import ChevronDown from "../../../../Assets/ChevronDownLargeDarkMode.svg";
import { useOutsideClickHandler } from "../../../../Hooks/useOutsideClick";
import { useDispatch } from "react-redux";
import { fetchMasterCategory } from "../../../../Features/settingsSlice";
import { useSelector } from "react-redux";

const targetAudience = [
  {
    id: "MEN",
    title: "Men",
  },
  {
    id: "WOMEN",
    title: "Women",
  },
  {
    id: "KIDS",
    title: "Kids",
  },
];

function StoreInfoForm({ storeInfo, closeForm }) {
  const defaultStoreInfo = {
    storeName: "",
    storeDescription: "",
    targetAudience: [],
    storeCategory: [],
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pinCode: "",
  };

  const defaultWarehouseAddress = {
    warehouseAddressLine1: "",
    warehouseAddressLine2: "",
    city: "",
    state: "",
    pinCode: "",
  };

  const defaultFormErrors = {
    storeName: "",
    storeDescription: "",
    targetAudience: "",
    storeCategory: "",
    addressLine1: "",
    warehouseAddressLine1: "",
    city: "",
    warehouseCity: "",
    state: "",
    warehouseState: "",
    pinCode: "",
    warehousePincode: "",
  };

  const [storeInfoInputs, setStoreInfoInputs] = useState(defaultStoreInfo);
  const [warehouseAddress, setWarehouseAddress] = useState(
    defaultWarehouseAddress
  );

  const [formErrors, setFormErrors] = useState(defaultFormErrors);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownwrapperRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const dropdownListRef = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchMasterCategory());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { categories } = useSelector((state) => state.settings.masterCategory);

  const categoryOptions = useMemo(() => {
    if (!Array.isArray(categories)) {
      return [];
    }

    return categories
      ?.filter((category) => category.isActive)
      ?.map((category) => ({
        label: category.name,
        value: category.keyName,
      }))
      .toReversed();
  }, [categories]);

  const handleDropdownButton = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useOutsideClickHandler(dropdownwrapperRef, () => {
    setDropdownOpen(false);
  });

  const handleSelectCategory = (category) => {
    setStoreInfoInputs((prev) => {
      const selected = prev.storeCategory || [];

      const updatedCategory = selected.includes(category.value)
        ? selected.filter((item) => item !== category.value)
        : [...selected, category.value];

      return {
        ...prev,
        storeCategory: updatedCategory,
      };
    });
  };

  function handleStoreChange(e) {
    const { name, value } = e.target;

    setStoreInfoInputs({ ...storeInfoInputs, [name]: value });
  }

  function handleWarehouseAddressChange(e) {
    const { name, value } = e.target;

    setWarehouseAddress({ ...warehouseAddress, [name]: value });
  }

  const handleSelectBoxClick = (id) => {
    setStoreInfoInputs((prev) => {
      const selected = prev.targetAudience || [];

      const updatedAudience = selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id];

      return {
        ...prev,
        targetAudience: updatedAudience,
      };
    });
  };

  async function handleSaveChanges() {
    const validationErrors = {};

    if (!storeInfoInputs.storeName) {
      validationErrors.storeName = "Required field";
    }

    if (storeInfoInputs.storeCategory.length === 0) {
      validationErrors.storeCategory = "Required field";
    }

    if (storeInfoInputs.targetAudience.length === 0) {
      validationErrors.targetAudience = "Required field";
    }

    if (!storeInfoInputs.storeDescription) {
      validationErrors.storeDescription = "Required field";
    }

    if (!storeInfoInputs.addressLine1) {
      validationErrors.addressLine1 = "Required field";
    }

    if (!storeInfoInputs.city) {
      validationErrors.city = "Required field";
    }

    if (!storeInfoInputs.state) {
      validationErrors.state = "Required field";
    }

    if (!storeInfoInputs.pinCode) {
      validationErrors.pinCode = "Required field";
    }

    if (!warehouseAddress.warehouseAddressLine1) {
      validationErrors.warehouseAddressLine1 = "Required field";
    }

    if (!warehouseAddress.city) {
      validationErrors.warehouseCity = "Required field";
    }

    if (!warehouseAddress.state) {
      validationErrors.warehouseState = "Required field";
    }

    if (!warehouseAddress.pinCode) {
      validationErrors.warehousePincode = "Required field";
    }

    if (Object.keys(validationErrors).length === 0) {
      setFormErrors(defaultFormErrors);

      try {
        const callBody = {
          storeName: storeInfoInputs.storeName,
          storeDescription: storeInfoInputs.storeDescription,
          storeCategory: storeInfoInputs.storeCategory,
          targetAudience: storeInfoInputs.targetAudience,
          storeAddress: `${storeInfoInputs.addressLine1}, ${storeInfoInputs.addressLine2}`,
          storeCity: storeInfoInputs.city,
          storeState: storeInfoInputs.state,
          storePincode: storeInfoInputs.pinCode,
          storeCountry: "India",
          warehouseAddr: {
            Addr: `${warehouseAddress.warehouseAddressLine1}, ${warehouseAddress.warehouseAddressLine2}`,
            city: warehouseAddress.city,
            state: warehouseAddress.state,
            pincode: warehouseAddress.pinCode,
            country: "India",
          },
        };

        const response = await updateStoreInfo(callBody);
        if (response.status === 200) {
          showToast("Store info updated.", "success");
          closeForm();
        }
      } catch (err) {
        showToast(err, "error");
      }
    } else {
      setFormErrors(validationErrors);
    }
  }

  useEffect(() => {
    if (storeInfo) {
      setStoreInfoInputs({
        storeName: storeInfo.storeName,
        storeDescription: storeInfo.storeDescription,
        storeCategory: storeInfo.storeCategory,
        targetAudience: storeInfo.targetAudience,
        addressLine1: storeInfo.storeAddress.trim(),
        addressLine2: "",
        city: storeInfo.storeCity,
        state: storeInfo.storeState,
        pinCode: storeInfo.storePincode,
      });
      setWarehouseAddress({
        warehouseAddressLine1: storeInfo.warehouseAddr.Addr.trim(),
        warehouseAddressLine2: "",
        city: storeInfo.warehouseAddr.city,
        state: storeInfo.warehouseAddr.state,
        pinCode: storeInfo.warehouseAddr.pincode,
      });
    }
  }, [storeInfo]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="font-semibold text-[20px] leading-6 text-[#121212]">
        Store Information
      </h1>

      <div className="flex flex-col gap-4">
        <div className="border border-[#EAEAEA] rounded-xl p-4 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <label
              htmlFor="storeName"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Store Name
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="storeName"
                id="storeName"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter store name"
                value={storeInfoInputs.storeName}
                onChange={handleStoreChange}
              />

              {formErrors.storeName && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.storeName}
                </p>
              )}
            </div>
          </div>

          <div
            className="flex items-center justify-between"
            ref={dropdownwrapperRef}
          >
            <label
              htmlFor="Category"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Select Category
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <div
                onClick={handleDropdownButton}
                ref={dropdownButtonRef}
                className="relative cursor-pointer flex items-center justify-between px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px]"
              >
                <p
                  className={`font-normal text-base select-none ${
                    storeInfoInputs.storeCategory?.length > 0
                      ? "text-primaryColor"
                      : "text-[#666666]"
                  }`}
                >
                  {storeInfoInputs.storeCategory?.length > 0
                    ? storeInfoInputs.storeCategory
                        .map(
                          (id) =>
                            categoryOptions.find(
                              (category) => category.value === id
                            )?.label
                        )
                        .filter(Boolean)
                        .join(", ")
                    : "select category"}
                </p>

                <div className="size-6 flex items-center justify-center select-none">
                  <img src={ChevronDown} alt="" />
                </div>

                {dropdownOpen && (
                  <div
                    ref={dropdownListRef}
                    className="bg-[#FFFFFF] max-h-52 overflow-y-scroll absolute z-10 top-[calc(100%+8px)] left-0 w-full rounded-[9px] shadow-[0px_3px_6px_0px_#0000001F]"
                  >
                    {categoryOptions.map((item) => {
                      const isSelected = storeInfoInputs.storeCategory?.some(
                        (value) => value === item.value
                      );

                      return (
                        <div
                          key={item.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCategory(item);
                          }}
                          className={`px-3 py-4 font-medium text-base flex gap-[10px] select-none items-center cursor-pointer ${
                            isSelected ? "text-primaryColor" : "text-[#666666]"
                          }`}
                        >
                          <div
                            className={`size-6 flex items-center justify-center rounded-md border transition ${
                              isSelected
                                ? "border-none bg-primaryColor"
                                : "border-[#D1D5DB]"
                            }`}
                          >
                            {isSelected && (
                              <img
                                className="select-none"
                                src={TickMark}
                                alt="Selected"
                              />
                            )}
                          </div>
                          {item.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {formErrors.storeCategory && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.storeCategory}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="targetAudience"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Target Audience
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <div className="flex items-center gap-3" id="targetAudience">
                {targetAudience?.map((item) => {
                  const isSelected = storeInfoInputs.targetAudience?.includes(
                    item.id
                  );
                  return (
                    <div
                      key={item.id}
                      className="inline-flex gap-2 items-center cursor-pointer"
                      onClick={() => handleSelectBoxClick(item.id)}
                    >
                      <div
                        className={`size-6 flex items-center justify-center rounded-md border transition ${
                          isSelected
                            ? "border-none bg-primaryColor"
                            : "border-[#D1D5DB]"
                        }`}
                      >
                        {isSelected && (
                          <img
                            className="select-none"
                            src={TickMark}
                            alt="Selected"
                          />
                        )}
                      </div>
                      <span className="font-urreg text-base">
                        {item?.title}
                      </span>
                    </div>
                  );
                })}
              </div>

              {formErrors.targetAudience && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.targetAudience}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <label
              htmlFor="storeDescription"
              className="cursor-pointer mt-3 font-medium text-[16px] leading-5 text-[#000000]"
            >
              Store Description
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <textarea
                name="storeDescription"
                id="storeDescription"
                className="resize-none outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                placeholder="enter store description"
                value={storeInfoInputs.storeDescription}
                onChange={handleStoreChange}
                rows={8}
              />

              {formErrors.storeDescription && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.storeDescription}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="addressLine1"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Address Line 1
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="addressLine1"
                id="addressLine1"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="address line 1"
                value={storeInfoInputs.addressLine1}
                onChange={handleStoreChange}
              />

              {formErrors.addressLine1 && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.addressLine1}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="addressLine2"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Address Line 2
            </label>

            <input
              name="addressLine2"
              id="addressLine2"
              className="outline-none w-[70%] border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
              type="text"
              placeholder="address line 2"
              value={storeInfoInputs.addressLine2}
              onChange={handleStoreChange}
            />
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="city"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              City
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="city"
                id="city"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter city"
                value={storeInfoInputs.city}
                onChange={handleStoreChange}
              />

              {formErrors.city && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.city}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="state"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              State
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="state"
                id="state"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter state"
                value={storeInfoInputs.state}
                onChange={handleStoreChange}
              />

              {formErrors.state && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.state}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="pinCode"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Pincode
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="pinCode"
                id="pinCode"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter pincode"
                value={storeInfoInputs.pinCode}
                onChange={handleStoreChange}
              />

              {formErrors.pinCode && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.pinCode}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p>Country</p>

            <div className="w-[70%] border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base text-[#666666]">
              <p>India</p>
            </div>
          </div>
        </div>

        <h1 className="font-semibold text-[20px] leading-6 text-[#121212]">
          Warehouse Address
        </h1>

        <div className="border border-[#EAEAEA] rounded-xl p-4 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <label
              htmlFor="warehouseAddressLine1"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Address Line 1
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="warehouseAddressLine1"
                id="warehouseAddressLine1"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="address line 1"
                value={warehouseAddress.warehouseAddressLine1}
                onChange={handleWarehouseAddressChange}
              />

              {formErrors.warehouseAddressLine1 && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.warehouseAddressLine1}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="warehouseAddressLine2"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Address Line 2
            </label>

            <input
              name="warehouseAddressLine2"
              id="warehouseAddressLine2"
              className="outline-none w-[70%] border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
              type="text"
              placeholder="address line 2"
              value={warehouseAddress.warehouseAddressLine2}
              onChange={handleWarehouseAddressChange}
            />
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="city"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              City
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="city"
                id="city"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter city"
                value={warehouseAddress.city}
                onChange={handleWarehouseAddressChange}
              />

              {formErrors.warehouseCity && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.warehouseCity}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="state"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              State
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="state"
                id="state"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter state"
                value={warehouseAddress.state}
                onChange={handleWarehouseAddressChange}
              />

              {formErrors.warehouseState && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.warehouseState}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label
              htmlFor="pinCode"
              className="cursor-pointer font-medium text-[16px] leading-5 text-[#000000]"
            >
              Pincode
            </label>

            <div className="w-[70%] flex flex-col gap-2">
              <input
                name="pinCode"
                id="pinCode"
                className="outline-none w-full border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base placeholder:text-[#666666] text-[#000000]"
                type="text"
                placeholder="enter pincode"
                value={warehouseAddress.pinCode}
                onChange={handleWarehouseAddressChange}
              />

              {formErrors.warehousePincode && (
                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                  {formErrors.warehousePincode}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p>Country</p>

            <div className="w-[70%] border border-[#EAEAEA] bg-[#F5F5F5] px-3 py-4 rounded-lg font-normal text-base text-[#666666]">
              <p>India</p>
            </div>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-3 font-semibold text-base text-[#121212]">
          <button
            onClick={closeForm}
            className="border border-[#808080] rounded px-4 py-2"
          >
            Cancel
          </button>

          <button
            onClick={handleSaveChanges}
            className="bg-primaryColor hover:bg-primaryAccent rounded px-4 py-2 text-buttonText"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default StoreInfoForm;
